import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { LineChart } from '../../components/charts/LineChart';
import { PieChart } from '../../components/charts/PieChart';
import { systemStatsService } from '../../services/admin';
import type { SystemStats } from '../../types/admin';

/**
 * System Statistics Page (Admin Only)
 *
 * Displays comprehensive system-wide metrics:
 * - User statistics (total, by role, verification rate)
 * - Account statistics (total, by plan)
 * - Campaign statistics (total, active, paused)
 * - Request statistics (total, recent, bot/human ratio)
 * - Traffic trends over time
 * - Top performing campaigns
 */
export const SystemStatsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch stats
  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemStatsService.getSystemStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system_stats.loading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // Prepare chart data
  const accountsByPlanData = Object.entries(stats.accounts.by_plan).map(([name, value]) => ({
    name,
    value: Number(value),
  }));

  return (
    <div className="space-y-5">
      {/* Page Header - More compact */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('system_stats.page_title')}</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {t('system_stats.page_subtitle')}
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="btn btn-secondary text-sm flex items-center gap-2"
          disabled={loading}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Overview Metrics - Redesigned with gradients and icons */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Users - Gradient background */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-5 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-blue-100">{t('system_stats.overview.total_users')}</p>
              <p className="mt-2 text-4xl font-bold">{stats.users.total}</p>
              <div className="mt-3 flex items-center gap-2 text-sm text-blue-100">
                <div className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{stats.users.verification_rate}% verified</span>
                </div>
              </div>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-blue-400/30 grid grid-cols-2 gap-2 text-xs text-blue-100">
            <div>New (30d): {stats.users.recent_30d}</div>
            <div>Admins: {stats.users.system_admins}</div>
          </div>
        </div>

        {/* Accounts */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-5 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-purple-100">{t('system_stats.overview.total_accounts')}</p>
              <p className="mt-2 text-4xl font-bold">{stats.accounts.total}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Campaigns */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-green-600 p-5 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-green-100">{t('system_stats.overview.total_campaigns')}</p>
              <p className="mt-2 text-4xl font-bold">{stats.campaigns.total}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-green-100">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-200"></span>
                  {stats.campaigns.active} active
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-300/50"></span>
                  {stats.campaigns.paused} paused
                </span>
              </div>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Requests */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 p-5 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-orange-100">{t('system_stats.overview.total_requests')}</p>
              <p className="mt-2 text-4xl font-bold">{stats.requests.total.toLocaleString()}</p>
              <p className="mt-3 text-sm text-orange-100">
                {stats.requests.bot_rate}% bot traffic
              </p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-orange-400/30 grid grid-cols-2 gap-2 text-xs text-orange-100">
            <div>24h: {stats.requests.last_24h.toLocaleString()}</div>
            <div>30d: {stats.requests.last_30d.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Tickets Statistics - Compact cards */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('system_stats.tickets.title')}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{t('system_stats.tickets.subtitle')}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-gray-900">{stats.tickets.total}</p>
            <p className="text-xs text-gray-600 mt-1">{t('system_stats.tickets.total')}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
            <p className="text-xl font-bold text-blue-700">{stats.tickets.open}</p>
            <p className="text-xs text-blue-600 mt-1">{t('system_stats.tickets.open')}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-100">
            <p className="text-xl font-bold text-yellow-700">{stats.tickets.in_progress}</p>
            <p className="text-xs text-yellow-600 mt-1 line-clamp-2">{t('system_stats.tickets.in_progress')}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-100">
            <p className="text-xl font-bold text-purple-700">{stats.tickets.waiting_user}</p>
            <p className="text-xs text-purple-600 mt-1 line-clamp-2">{t('system_stats.tickets.waiting_user')}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
            <p className="text-xl font-bold text-green-700">{stats.tickets.resolved}</p>
            <p className="text-xs text-green-600 mt-1">{t('system_stats.tickets.resolved')}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
            <p className="text-xl font-bold text-gray-700">{stats.tickets.closed}</p>
            <p className="text-xs text-gray-600 mt-1">{t('system_stats.tickets.closed')}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
            <p className="text-xl font-bold text-red-700">{stats.tickets.unassigned}</p>
            <p className="text-xs text-red-600 mt-1">{t('system_stats.tickets.unassigned')}</p>
          </div>
        </div>
      </div>

      {/* Revenue Statistics - Larger and more impactful */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="rounded-full bg-emerald-500 p-2">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('system_stats.revenue.title')}</h3>
            <p className="text-xs text-gray-600">{t('system_stats.revenue.subtitle')}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-emerald-100 shadow-sm">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2">{t('system_stats.revenue.last_7_days')}</p>
            <p className="text-3xl font-bold text-emerald-600">
              ${stats.revenue.last_7_days.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-emerald-100 shadow-sm">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2">{t('system_stats.revenue.last_30_days')}</p>
            <p className="text-3xl font-bold text-emerald-600">
              ${stats.revenue.last_30_days.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-teal-100 shadow-sm">
            <p className="text-xs font-medium text-teal-700 uppercase tracking-wide mb-2">{t('system_stats.revenue.last_month')}</p>
            <p className="text-3xl font-bold text-teal-600">
              ${stats.revenue.last_month.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-teal-100 shadow-sm">
            <p className="text-xs font-medium text-teal-700 uppercase tracking-wide mb-2">{t('system_stats.revenue.month_to_date')}</p>
            <p className="text-3xl font-bold text-teal-600">
              ${stats.revenue.month_to_date.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Charts Row - Improved layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Daily Requests Trend - Takes 2 columns */}
        <div className="lg:col-span-2">
          <Card>
            <LineChart
              data={stats.trends.daily_requests}
              title={t('system_stats.charts.daily_requests')}
              xAxisKey="date"
              lines={[
                {
                  dataKey: 'count',
                  stroke: '#3b82f6',
                  name: t('system_stats.overview.requests'),
                },
              ]}
              height={280}
            />
          </Card>
        </div>

        {/* Accounts by Plan - Takes 1 column */}
        {accountsByPlanData.length > 0 && (
          <div className="lg:col-span-1">
            <Card>
              <PieChart
                data={accountsByPlanData}
                title={t('system_stats.charts.accounts_by_plan')}
                height={280}
              />
            </Card>
          </div>
        )}
      </div>

      {/* Top Campaigns - Enhanced table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-indigo-100 p-2">
              <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('system_stats.top_campaigns.title')}
            </h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  {t('system_stats.top_campaigns.campaign_name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  {t('system_stats.top_campaigns.account')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  {t('system_stats.top_campaigns.requests')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats.top_campaigns.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-sm text-gray-500">{t('system_stats.top_campaigns.no_campaigns')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                stats.top_campaigns.map((campaign, index) => (
                  <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-indigo-600">#{index + 1}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{campaign.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Link
                        to={`/admin/accounts?search=${encodeURIComponent(campaign.account_name)}`}
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                      >
                        {campaign.account_name}
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-50 text-blue-700">
                        {campaign.requests_count.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

SystemStatsPage.displayName = 'SystemStatsPage';
